import React, { Dispatch, useEffect, useState } from 'react';

import { PlayType, RatchetModels, ServiceLaunch } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';

import { isEmpty } from 'lodash';
import { Input } from 'src/components/Input';
import { TerritoryFlag } from 'src/components/TerritoryFlag';
import { ContractTermSection } from 'src/components/shared/ContractTermSection';
import { InputValidationRuleType } from 'src/constants/enums';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IMetadata } from 'src/types/IMetadata';
import { IPrimeAMFRateCardValidationAction } from 'src/types/IPrimeAMFRateCardValidationAction';
import { IPrimeAMFRateCardValuesAction } from 'src/types/IPrimeAMFRateCardValuesAction';
import { IPrimeAMFRateCardValues } from 'src/types/IRateCard';

import { IPrimeAMFRateCardErrors } from '../../rateCardValidationReducer';
import { AddConversionModel } from '../AddConversionModel';
import { AddPlayType } from '../AddPlayType';
import './AddAdRevShare.scss';

interface IAddAdRevShare {
    rateCardValues: IPrimeAMFRateCardValues;
    dispatchRateCardValues: Dispatch<IPrimeAMFRateCardValuesAction>;
    rateCardErrors: IPrimeAMFRateCardErrors;
    dispatchRateCardErrors: Dispatch<IPrimeAMFRateCardValidationAction>;
    playTypes: PlayType[];
    conversionModels: RatchetModels;
    augmentedMetadata: IMetadata;
}

export const AddAdRevShare: React.FC<IAddAdRevShare> = ({
    rateCardValues,
    dispatchRateCardValues,
    rateCardErrors,
    dispatchRateCardErrors,
    playTypes,
    conversionModels,
    augmentedMetadata
}: IAddAdRevShare) => {
    const { metadata } = React.useContext(AppContext);
    const [isToggled, setIsToggled] = useState(
        [
            rateCardValues.serviceId === 3
                ? rateCardValues.amfContractTerms?.adRevShare.revSharePercent
                : rateCardValues.primeContractTerms?.adRevShare?.revSharePercent,
            rateCardValues.serviceId === 3
                ? rateCardValues.amfContractTerms?.adRevShare.playTypes
                : rateCardValues.primeContractTerms?.adRevShare?.playTypes,
            rateCardValues.serviceId === 3
                ? rateCardValues.amfContractTerms?.adRevShare.conversions
                : rateCardValues.primeContractTerms?.adRevShare?.conversions
        ].some((key) => !isEmpty(key))
    );
    const modelErrorMessage = rateCardErrors.amfContractTerms.adRevShare.model;
    const revShareErrorMessage = rateCardErrors.amfContractTerms.adRevShare.revSharePercent;
    const services = metadata.serviceMapping;
    const [launchedTerritoryList, setLaunchedTerritoryList] = useState<ServiceLaunch[]>([]);

    // Validate on rate card change
    useEffect(() => {
        dispatchRateCardErrors({
            type: 'validate_ad_rev_share',
            rateCardValues,
            isToggled
        });
    }, [
        rateCardValues.serviceId === 3
            ? rateCardValues.amfContractTerms?.adRevShare.playTypes
            : rateCardValues.primeContractTerms?.adRevShare?.revSharePercent,
        rateCardValues.serviceId === 3 ? rateCardValues.amfContractTerms?.adRevShare.conversions : null,
        isToggled
    ]);

    useEffect(() => {
        const serviceLaunches = augmentedMetadata.activeServiceLaunchDateMapping![rateCardValues.serviceId];
        setLaunchedTerritoryList(serviceLaunches);
    }, [augmentedMetadata.activeServiceLaunchDateMapping]);

    const handleToggle = () => {
        if (isToggled) {
            dispatchRateCardValues({ type: 'reset_ad_rev_share' });
        } else {
            dispatchRateCardErrors({
                type: 'validate_is_next_disabled',
                rateCardValues,
                stepId: ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS
            });
        }

        setIsToggled((prevState) => !prevState);
    };

    const handleRevShareErrorMessageChange = (message: string): void => {
        dispatchRateCardErrors({
            type: 'validate_rev_share',
            rateCardValues,
            message
        });
    };

    const handleTerritoryToggle = (territoryCode: string, shouldBeToggled: boolean): void => {
        const currentTerritories = rateCardValues.primeContractTerms?.adRevShare?.territories || [];

        let updatedTerritories;
        if (shouldBeToggled) {
            updatedTerritories = [...currentTerritories, territoryCode];
        } else {
            updatedTerritories = currentTerritories.filter((code) => code !== territoryCode);
        }

        dispatchRateCardValues({
            type: 'set_ad_rev_share_territories',
            territories: updatedTerritories
        });
    };

    return (
        <Column>
            <Row alignmentHorizontal="justify">
                <Text type="h500">
                    Advertising revenue share{rateCardValues.serviceId === 3 && ' vs cost per play'}
                </Text>
                <Toggle checked={isToggled} onChange={handleToggle} />
            </Row>
            {isToggled && (
                <>
                    {!isEmpty(modelErrorMessage) && (
                        <Alert type="error" size="medium">
                            {modelErrorMessage}
                        </Alert>
                    )}
                    <ContractTermSection title={'Advertising revenue share'}>
                        <Input
                            label="Revenue share percentage *"
                            value={rateCardValues.primeContractTerms?.adRevShare?.revSharePercent ?? ''}
                            onChange={(value) =>
                                dispatchRateCardValues({
                                    type: 'set_ad_rev_share_percentage',
                                    revSharePercent: value
                                })
                            }
                            validationRules={[
                                InputValidationRuleType.Required,
                                InputValidationRuleType.Max8Char,
                                InputValidationRuleType.GreaterEqual0LessEqual1
                            ]}
                            errorMessage={revShareErrorMessage}
                            handleErrMsgChange={handleRevShareErrorMessageChange}
                            type="number"
                            isEditMode={true}
                        />
                        {rateCardValues.serviceId === services[2].serviceId ? (
                            <Table showStripes={false} showDividers={false}>
                                {launchedTerritoryList.map(({ territoryID, territoryCode }) => {
                                    return (
                                        <TableRow key={territoryID}>
                                            <TableCell>
                                                <TerritoryFlag
                                                    territoryCode={territoryCode!}
                                                    showTerritoryName={true}
                                                    showTerritoryCode={false}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Toggle
                                                    checked={rateCardValues.primeContractTerms?.adRevShare?.territories?.includes(
                                                        territoryCode as string
                                                    )}
                                                    onChange={(val) => handleTerritoryToggle(territoryCode!, val)}
                                                    data-testid={`household-sharing-toggle-${territoryCode}`}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </Table>
                        ) : (
                            <></>
                        )}
                    </ContractTermSection>
                    {rateCardValues.serviceId === 3 && (
                        <>
                            <AddPlayType
                                rateCardValues={rateCardValues}
                                dispatchRateCardValues={dispatchRateCardValues}
                                rateCardErrors={rateCardErrors}
                                dispatchRateCardErrors={dispatchRateCardErrors}
                                playTypes={playTypes}
                            />
                            <AddConversionModel
                                rateCardValues={rateCardValues}
                                dispatchRateCardValues={dispatchRateCardValues}
                                conversionModels={conversionModels}
                            />
                        </>
                    )}
                </>
            )}
        </Column>
    );
};
